// Prospection automatique (D-086) : libellés de la console, aperçu des messages, taille estimée.
// Le rendu des e-mails qui partent est fait par la base (_prospect_fill / _prospect_render) ; ce module
// applique LA MÊME règle pour l'aperçu à l'écran (champs surlignés) : tests dans prospect.test.ts.
import { tierFor } from './console';

export type ProspectStatus = 'to_contact' | 'contacted' | 'followed_up' | 'clicked' | 'signed' | 'not_interested' | 'unsubscribed' | 'bounced';
/** « adversaire » : joue contre un club d'Arceau (annuaire des clubs, D-094). */
export type ProspectSource = 'ffbb' | 'derogation' | 'parrainage' | 'manuel' | 'adversaire';
export type ProspectBlock = 'no_email' | 'excluded' | 'optout' | 'derog_optout' | 'on_arceau' | 'bounced' | 'not_interested';

export const STATUS_ORDER: ProspectStatus[] = ['to_contact', 'contacted', 'followed_up', 'clicked', 'signed', 'not_interested', 'unsubscribed', 'bounced'];
export const STATUS_LABEL: Record<ProspectStatus, string> = {
  to_contact: 'À contacter', contacted: 'Contacté', followed_up: 'Relancé', clicked: 'Cliqué', signed: 'Inscrit',
  not_interested: 'Pas intéressé', unsubscribed: 'Désinscrit', bounced: 'Erreur',
};
/** Classe de la pastille de chaque statut (mêmes couleurs que les maquettes P1). */
export const STATUS_CHIP: Record<ProspectStatus, string> = {
  to_contact: 'chip-soft', contacted: 'chip-mute', followed_up: 'pr-info', clicked: 'chip-accent', signed: 'chip-ok',
  not_interested: 'chip-soft', unsubscribed: 'chip-ko', bounced: 'chip-ko',
};
export const SOURCE_LABEL: Record<ProspectSource, string> = { ffbb: 'FFBB', derogation: 'Dérogation', parrainage: 'Recommandation', manuel: 'À la main', adversaire: 'Adversaire' };
export const BLOCK_LABEL: Record<ProspectBlock, string> = {
  no_email: 'Pas d’e-mail sur la fiche FFBB',
  excluded: 'Ne pas contacter (à la main)',
  optout: 'Désinscrit : plus jamais d’e-mail',
  derog_optout: 'Désinscrit des e-mails de dérogation',
  on_arceau: 'Déjà sur Arceau',
  bounced: 'Adresse en erreur',
  not_interested: 'Pas intéressé : rien pendant 12 mois',
};

// --- Champs du message ---------------------------------------------------------------------------
export type FieldKey = 'club' | 'championnat' | 'club_voisin' | 'comite';
export const FIELDS: { key: FieldKey; label: string }[] = [
  { key: 'club', label: 'club' }, { key: 'championnat', label: 'championnat' },
  { key: 'club_voisin', label: 'club voisin sur Arceau' }, { key: 'comite', label: 'comité' },
];
export type Fills = Partial<Record<FieldKey, string | null>>;
export interface Seg { text: string; field?: FieldKey }

const TOKEN = /\{(club_voisin|club|championnat|comite)\}/g;

/** Découpe un texte en morceaux : texte simple et champs ({club}…). */
export function splitTokens(text: string): Seg[] {
  const out: Seg[] = [];
  let at = 0;
  for (const m of text.matchAll(TOKEN)) {
    if (m.index! > at) out.push({ text: text.slice(at, m.index) });
    out.push({ text: m[0], field: m[1] as FieldKey });
    at = m.index! + m[0].length;
  }
  if (at < text.length) out.push({ text: text.slice(at) });
  return out;
}

const VOWEL = /^[aeiouyhàâäéèêëîïôöûü]/i;
/** Les paragraphes d'un modèle rempli, champs marqués (pour les surligner). « de » s'élide devant une
 *  voyelle (« d’Inter-départementale »). Un paragraphe qui cite {club_voisin} disparaît s'il n'y a pas
 *  de club voisin qui accepte d'être cité. */
export function fillTemplate(tpl: string, fills: Fills): Seg[][] {
  const out: Seg[][] = [];
  for (const raw of tpl.replace(/\r/g, '').split(/\n[ \t]*\n/)) {
    if (raw.includes('{club_voisin}') && !fills.club_voisin) continue;
    const segs: Seg[] = [];
    for (const s of splitTokens(raw.replace(/^[ \t\n]+|[ \t\n]+$/g, ''))) {
      if (!s.field) { segs.push({ ...s }); continue; }
      const value = fills[s.field] ?? '';
      const prev = segs[segs.length - 1];
      if (prev && s.field !== 'club_voisin' && VOWEL.test(value) && /(^|[^\p{L}])de $/u.test(prev.text)) prev.text = `${prev.text.slice(0, -3)}d’`;
      segs.push({ text: value, field: s.field });
    }
    const kept = segs.filter(s => s.text !== '');
    if (kept.length) out.push(kept);
  }
  return out;
}

/** Le texte brut des paragraphes (comme le texte de l'e-mail). */
export const paragraphsText = (p: Seg[][]): string[] => p.map(segs => segs.map(s => s.text).join(''));

// --- Fiche FFBB ------------------------------------------------------------------------------------
export interface Championship { competition?: string | null; category?: string | null; sex?: string | null; poule?: string | null }

const CAT_ORDER = ['SE', 'U21', 'U20', 'U19', 'U18', 'U17', 'U16', 'U15', 'U14', 'U13', 'U12', 'U11', 'U10', 'U9', 'U8', 'U7'];
const catLabel = (c: string) => (c === 'SE' ? 'Seniors' : c);

/** « Seniors F et M, U17 M, U15 F, U9 (2) » d'après les engagements. */
export function teamsSummary(list: Championship[]): string {
  const by = new Map<string, { sexes: string[]; n: number }>();
  for (const e of list) {
    const c = (e.category ?? '').toUpperCase() || '?';
    const g = by.get(c) ?? { sexes: [], n: 0 };
    g.n += 1;
    const s = (e.sex ?? '').toUpperCase();
    if (s && !g.sexes.includes(s)) g.sexes.push(s);
    by.set(c, g);
  }
  const rank = (c: string) => { const i = CAT_ORDER.indexOf(c); return i < 0 ? 99 : i; };
  return [...by.entries()].sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0])).map(([c, g]) => {
    const sexes = g.sexes.sort((a, b) => (a === 'F' ? -1 : b === 'F' ? 1 : 0)).join(' et ');
    const many = g.n > Math.max(g.sexes.length, 1) ? ` (${g.n})` : '';
    return `${c === '?' ? 'Autres' : catLabel(c)}${sexes ? ` ${sexes}` : ''}${many}`;
  }).join(', ');
}

/** Les championnats, sans doublon, dans l'ordre de la fiche (le 1er sert pour {championnat}). */
export function championshipsSummary(list: Championship[], max = 3): string {
  const names = [...new Set(list.map(e => (e.competition ?? '').trim()).filter(Boolean))];
  return names.slice(0, max).join(' · ') + (names.length > max ? ' …' : '');
}

const round10 = (n: number) => Math.max(10, Math.round(n / 10) * 10);
/** Taille estimée : de 12 à 20 licenciés par équipe engagée (mini-basket compris), et les paliers D-078. */
export function sizeRange(teams: number): { low: number; high: number; tiers: string } | null {
  if (!teams || teams < 1) return null;
  const low = round10(teams * 12), high = round10(teams * 20);
  const a = tierFor(low), b = tierFor(high);
  const tiers = !a ? 'devis' : a === b ? `palier ${a}` : b ? `palier ${a} ou ${b}` : `palier ${a} ou devis`;
  return { low, high, tiers };
}

/** Filtre « Taille du club » : la valeur envoyée à la base (palier, 0 = au-delà de 1 000). */
export const SIZE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Toutes les tailles' }, { value: '100', label: 'Jusqu’à 100 licenciés' }, { value: '200', label: '100 à 200' },
  { value: '300', label: '200 à 300' }, { value: '500', label: '300 à 500' }, { value: '1000', label: '500 à 1 000' }, { value: '0', label: 'Plus de 1 000' },
];

// --- La campagne ------------------------------------------------------------------------------------
export const DAY_SHORT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const DAY_LONG = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

/** « 9 h », « 11 h 30 » d'après « 09:00 », « 11:30 ». */
export function hourLabel(hm: string): string {
  const [h, m] = hm.split(':').map(Number);
  return `${h} h${m ? ` ${String(m).padStart(2, '0')}` : ''}`;
}

const isoDow = (d: Date) => ((d.getDay() + 6) % 7) + 1;
const minutes = (hm: string) => { const [h, m] = hm.split(':').map(Number); return (h ?? 0) * 60 + (m ?? 0); };

/** Le prochain envoi, en mots : « aujourd’hui à 9 h », « en cours, jusqu’à 11 h 30 », « demain à 9 h », « lundi à 9 h ». */
export function nextSend(c: { days: number[]; hour_from: string; hour_to: string }, now = new Date()): string {
  const t = now.getHours() * 60 + now.getMinutes();
  const today = isoDow(now);
  if (c.days.includes(today)) {
    if (t < minutes(c.hour_from)) return `aujourd’hui à ${hourLabel(c.hour_from)}`;
    if (t < minutes(c.hour_to)) return `en cours, jusqu’à ${hourLabel(c.hour_to)}`;
  }
  for (let i = 1; i <= 7; i++) {
    const dow = ((today - 1 + i) % 7) + 1;
    if (c.days.includes(dow)) return `${i === 1 ? 'demain' : DAY_LONG[dow - 1]} à ${hourLabel(c.hour_from)}`;
  }
  return '—';
}

/** « du lundi au vendredi », « lun., mer. et ven. », « tous les jours ». */
export function daysLabel(days: number[]): string {
  const d = [...new Set(days)].sort((a, b) => a - b);
  if (d.length === 7) return 'tous les jours';
  const run = d.length > 2 && d.every((x, i) => i === 0 || x === d[i - 1]! + 1);
  if (run) return `du ${DAY_LONG[d[0]! - 1]} au ${DAY_LONG[d[d.length - 1]! - 1]}`;
  const names = d.map(x => DAY_LONG[x - 1]!);
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} et ${names[names.length - 1]}` : names[0] ?? '';
}

/** Combien de temps pour écrire à toute la liste : « 8 mois environ », « 3 semaines environ ». */
export function durationLabel(clubs: number, perDay: number, daysPerWeek: number): string {
  if (!clubs || !perDay || !daysPerWeek) return '';
  const weeks = clubs / (perDay * daysPerWeek);
  if (weeks < 1) return 'moins d’une semaine';
  if (weeks < 8) return `${Math.round(weeks)} semaine${Math.round(weeks) > 1 ? 's' : ''} environ`;
  const months = Math.round(weeks / 4.35);
  return months >= 24 ? `${Math.round(months / 12)} ans environ` : `${months} mois environ`;
}
